import { configManager } from "@/features/client-config/services/config-manager";
import { delay } from "@/features/radar/utils/delay/delay";
import { networkDebugMonitor } from "@/services/monitoring/network-debug-monitor/network-debug-monitor";
import { storageManager } from "@/services/storage/storage/storage";
import { captureException } from "@/utils/sentry";
import { joinUrl } from "@/utils/url";

import { toIfModifiedSince } from "./radar-snapshot-utils";

const MAX_CONCURRENT_CAPTURES = 3;
const SNAPSHOT_POLL_INTERVAL_MS = 1_000;
const SNAPSHOT_POLL_MAX_ATTEMPTS = 10;
const TEST_SNAPSHOT_MAX_ATTEMPTS = 5;
const SNAPSHOT_ENDPOINT = "api/KioskSnapshot";
const SERVICE_NAME = "radar-snapshot";

type QueuedCapture = {
  radarId: string;
  shotId: string;
  shotTimestamp: number;
  resolve: () => void;
};

type SnapshotResult =
  | { kind: "image"; blob: Blob }
  | { kind: "not-modified" }
  | { kind: "no-content" }
  | { kind: "error" }
  | { kind: "aborted" };

/**
 * Radar Snapshot Service
 *
 * Polls GET /api/KioskSnapshot after a shot using If-Modified-Since conditional
 * requests. Stores the first valid JPEG frame written after the shot into
 * IndexedDB keyed by shotId. UI hooks read from IndexedDB and are unaffected
 * by this polling strategy.
 *
 * Endpoint contract (snapshot-endpoint-technical-doc.md):
 *   200 → valid JPEG (new frame available)
 *   204 → no usable file yet (keep polling)
 *   304 → frame unchanged since our If-Modified-Since (keep polling)
 */
class RadarSnapshotService {
  private activeControllers = new Map<string, AbortController>();
  private inFlightCount = 0;
  private queue: QueuedCapture[] = [];
  /** Unique identity of the current capture session; replaced on abortAll so stale captures self-cancel. */
  private sessionToken = Symbol();

  private async getRadarBaseUrl(radarId: string): Promise<string | null> {
    const config = await configManager.getConfig();
    const url = config?.radars.find(
      (radar) => radar.id === radarId
    )?.radarHttpBaseUrl;
    return url?.trim() || null;
  }

  /**
   * Fetches the KioskSnapshot endpoint once and returns a typed result.
   * Sends If-Modified-Since when provided. A zero-byte 200 body is treated
   * as no-content because the device excludes files still being flushed.
   */
  private async fetchSnapshotOnce(
    url: string,
    options: { ifModifiedSince?: string; signal: AbortSignal; radarId: string }
  ): Promise<SnapshotResult> {
    const headers: Record<string, string> = {};
    if (options.ifModifiedSince) {
      headers["If-Modified-Since"] = options.ifModifiedSince;
    }

    const startedAt = performance.now();

    try {
      const response = await fetch(url, {
        method: "GET",
        headers,
        signal: options.signal,
      });

      const contentLength = response.headers.get("Content-Length");
      const contentType = response.headers.get("Content-Type") || "unknown";
      networkDebugMonitor.logHttpRequest({
        service: SERVICE_NAME,
        method: "GET",
        url,
        status: response.status,
        durationMs: performance.now() - startedAt,
        responseBody:
          response.status === 200
            ? `binary (${contentType}${contentLength ? `, ${contentLength} bytes` : ""}) — not shown`
            : undefined,
        radarId: options.radarId,
      });

      if (response.status === 304) {
        return { kind: "not-modified" };
      }

      if (response.status === 204) {
        return { kind: "no-content" };
      }

      if (!response.ok) {
        console.error(
          "❌ [RadarSnapshot] Unexpected status:",
          response.status,
          response.statusText
        );
        return { kind: "error" };
      }

      const blob = await response.blob();
      if (blob.size === 0) {
        return { kind: "no-content" };
      }

      return { kind: "image", blob };
    } catch (error) {
      if (options.signal.aborted) {
        return { kind: "aborted" };
      }
      networkDebugMonitor.logHttpRequest({
        service: SERVICE_NAME,
        method: "GET",
        url,
        durationMs: performance.now() - startedAt,
        error: error instanceof Error ? error.message : String(error),
        radarId: options.radarId,
      });
      console.error("❌ [RadarSnapshot] Fetch error:", error);
      return { kind: "error" };
    }
  }

  private drainQueue(): void {
    while (
      this.inFlightCount < MAX_CONCURRENT_CAPTURES &&
      this.queue.length > 0
    ) {
      const next = this.queue.shift();
      if (!next) {
        return;
      }
      void this.runCapture(
        next.radarId,
        next.shotId,
        next.shotTimestamp
      ).finally(next.resolve);
    }
  }

  private isCancelled(controller: AbortController, token: symbol): boolean {
    return controller.signal.aborted || token !== this.sessionToken;
  }

  private reportPollError(shotId: string, attempt: number): void {
    console.error(
      `❌ [RadarSnapshot] Poll error on attempt ${attempt + 1} for shot ${shotId}`
    );
    if (!import.meta.env.DEV) {
      captureException(new Error("KioskSnapshot poll error"), {
        service: "radar-snapshot",
        operation: "poll",
        shotId,
        attempt,
      });
    }
  }

  /**
   * Polls KioskSnapshot until a post-shot frame is available, then stores it.
   * Returns true when a frame was stored, false when max attempts are exhausted.
   */
  private async pollForPostShotFrame(
    radarId: string,
    shotId: string,
    url: string,
    ifModifiedSince: string,
    controller: AbortController,
    token: symbol
  ): Promise<boolean> {
    for (let attempt = 0; attempt < SNAPSHOT_POLL_MAX_ATTEMPTS; attempt++) {
      if (this.isCancelled(controller, token)) {
        return false;
      }

      const result = await this.fetchSnapshotOnce(url, {
        ifModifiedSince,
        signal: controller.signal,
        radarId,
      });

      if (result.kind === "image") {
        if (this.isCancelled(controller, token)) {
          return false;
        }
        await storageManager.initialize();
        await storageManager.storeSnapshot(shotId, result.blob);
        console.log(`✅ [RadarSnapshot] Snapshot stored for shot ${shotId}`);
        return true;
      }

      if (result.kind === "aborted") {
        return false;
      }

      if (result.kind === "error") {
        this.reportPollError(shotId, attempt);
      }

      if (attempt < SNAPSHOT_POLL_MAX_ATTEMPTS - 1) {
        await delay(SNAPSHOT_POLL_INTERVAL_MS, controller.signal);
      }
    }

    console.warn(
      `⚠️ [RadarSnapshot] No post-shot frame found after ${SNAPSHOT_POLL_MAX_ATTEMPTS} attempts for shot ${shotId}`
    );
    return false;
  }

  private async runCapture(
    radarId: string,
    shotId: string,
    shotTimestamp: number
  ): Promise<void> {
    const token = this.sessionToken;
    const controller = new AbortController();
    this.activeControllers.set(shotId, controller);
    this.inFlightCount += 1;

    try {
      const baseUrl = await this.getRadarBaseUrl(radarId);
      if (!baseUrl || this.isCancelled(controller, token)) {
        return;
      }

      const url = joinUrl(baseUrl, SNAPSHOT_ENDPOINT);
      const ifModifiedSince = toIfModifiedSince(shotTimestamp);

      await this.pollForPostShotFrame(
        radarId,
        shotId,
        url,
        ifModifiedSince,
        controller,
        token
      );
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      console.error("❌ [RadarSnapshot] Capture failed:", error);
      if (!import.meta.env.DEV) {
        captureException(error, {
          service: "radar-snapshot",
          operation: "capture",
          shotId,
        });
      }
    } finally {
      this.activeControllers.delete(shotId);
      this.inFlightCount -= 1;
      this.drainQueue();
    }
  }

  /**
   * Captures a snapshot for a shot by polling KioskSnapshot until a frame
   * written after shotTimestamp is available. Queued when at concurrency limit.
   *
   * @param shotId - Identifier used as the IndexedDB key for the stored blob.
   * @param shotTimestamp - Unix ms epoch from the shot WebSocket event (radar
   *   device clock). Used to construct the If-Modified-Since header so only
   *   frames written after the shot are accepted.
   */
  async captureForShot(
    radarId: string,
    shotId: string,
    shotTimestamp: number
  ): Promise<void> {
    if (this.inFlightCount < MAX_CONCURRENT_CAPTURES) {
      return this.runCapture(radarId, shotId, shotTimestamp);
    }

    return new Promise<void>((resolve) => {
      this.queue.push({ radarId, shotId, shotTimestamp, resolve });
      this.drainQueue();
    });
  }

  /**
   * Aborts all in-flight captures and clears the pending queue (session end).
   */
  abortAll(): void {
    this.sessionToken = Symbol();

    for (const controller of this.activeControllers.values()) {
      controller.abort();
    }
    this.activeControllers.clear();

    for (const pending of this.queue) {
      pending.resolve();
    }
    this.queue = [];
  }

  /**
   * Fetches a frame from KioskSnapshot for the debug modal, using the same
   * If-Modified-Since polling flow as captureForShot.
   *
   * @param requestedAt - Unix ms epoch captured at the moment the user clicks
   *   "take snapshot". Frames written before this time are rejected, matching
   *   the behaviour of a real shot capture.
   */
  async testSnapshot(
    radarId: string,
    requestedAt: number
  ): Promise<Blob | null> {
    const baseUrl = await this.getRadarBaseUrl(radarId);
    if (!baseUrl) {
      throw new Error("No radar HTTP base URL configured");
    }

    const controller = new AbortController();
    const url = joinUrl(baseUrl, SNAPSHOT_ENDPOINT);
    const ifModifiedSince = toIfModifiedSince(requestedAt);

    for (let attempt = 0; attempt < TEST_SNAPSHOT_MAX_ATTEMPTS; attempt++) {
      const result = await this.fetchSnapshotOnce(url, {
        ifModifiedSince,
        signal: controller.signal,
        radarId,
      });

      if (result.kind === "image") {
        return result.blob;
      }

      if (result.kind === "aborted" || result.kind === "error") {
        return null;
      }

      if (attempt < TEST_SNAPSHOT_MAX_ATTEMPTS - 1) {
        await delay(SNAPSHOT_POLL_INTERVAL_MS, controller.signal);
      }
    }

    return null;
  }

  /**
   * Clears all snapshots from IndexedDB (called when starting a new session).
   */
  async clearAllSnapshots(): Promise<void> {
    try {
      await storageManager.initialize();
      await storageManager.clearAllSnapshots();
    } catch (error) {
      console.error("❌ [RadarSnapshot] Failed to clear all snapshots:", error);
    }
  }
}

export const radarSnapshotService = new RadarSnapshotService();
